/**
 * MODULE: domain/pages/frontend/api/pageBrainActivityApi.ts
 * PURPOSE: Fetch world-model page activity from data-management for unified timeline.
 * DEPENDENCIES: @shared/lib/frontend/api/config
 * PUBLIC_API: getPageBrainActivity, PageBrainActivityEvent
 * SIDE_EFFECTS: HTTP GET /api/v1/activity-events/by-page/{pageId}
 * AGENT_SAFETY: RESTRICTED
 */

import axios from 'axios';
import { apiV1Url, getAuthHeaders } from '@shared/lib/frontend/api/config';
import type { PageBrainActivityScope } from '../utils/pageBrainActivityScope';

export type PageBrainActivityEvent = {
  id: string;
  tenantId: string;
  eventType: string;
  source: string;
  externalSourceId: string;
  occurredAtISO: string;
  title: string;
  subtitle?: string;
  actorUserId?: string;
  payload?: Record<string, unknown>;
};

type PageBrainActivityResponse = {
  items: PageBrainActivityEvent[];
};

export async function getPageBrainActivity(
  pageId: string,
  limit = 25,
  scope: PageBrainActivityScope = {},
  eventIds: string[] = [],
): Promise<PageBrainActivityEvent[]> {
  const pid = (pageId || '').trim();
  if (!pid) return [];

  const search = new URLSearchParams();
  search.set('limit', String(limit));
  if (scope.dealId) search.set('deal_id', scope.dealId);
  if (scope.contactId) search.set('contact_id', scope.contactId);
  if (scope.companyId) search.set('company_id', scope.companyId);
  const ids = eventIds.map((id) => id.trim()).filter(Boolean);
  if (ids.length) search.set('event_ids', ids.join(','));
  const url = apiV1Url(`/activity-events/by-page/${encodeURIComponent(pid)}?${search.toString()}`);
  const { data } = await axios.get<PageBrainActivityResponse>(url, {
    headers: getAuthHeaders(),
  });
  return data.items ?? [];
}
